import * as React from "react";
import type { ReactNode } from "react";
import { CheckIcon, MoonIcon, MonitorIcon, SunIcon } from "lucide-react";
import { useTranslations } from "@/i18n";

import { useTheme } from "@/shared/theme/components/use-theme";
import { Button } from "@ibada/ui";
import { cn } from "@/shared/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@ibada/ui";
import { ThemeMode } from "@/shared/theme/lib/types";

type ThemeModeMeta = {
  label: string;
  icon: ReactNode;
};

const THEME_MODE_META: Record<ThemeMode, ThemeModeMeta> = {
  [ThemeMode.Light]: { label: "Light", icon: <SunIcon className='size-4' /> },
  [ThemeMode.Dark]: { label: "Dark", icon: <MoonIcon className='size-4' /> },
  [ThemeMode.System]: { label: "System", icon: <MonitorIcon className='size-4' /> },
};

type ThemeModeSwitcherProps = {
  iconOnly?: boolean;
  className?: string;
  modes?: ThemeMode[];
  variant?: "outline" | "ghost";
};

export function ThemeModeSwitcher({
  iconOnly = false,
  className,
  modes = [ThemeMode.Light, ThemeMode.Dark, ThemeMode.System],
  variant = "outline",
}: ThemeModeSwitcherProps) {
  const t = useTranslations("settings.themeModeSwitcher");
  const { config, setMode } = useTheme();
  const activeMode = config.mode;
  const visibleModes = modes.length > 0 ? modes : [ThemeMode.Light, ThemeMode.Dark, ThemeMode.System];
  const displayMode = visibleModes.includes(activeMode) ? activeMode : visibleModes[0];
  const fallbackMode = visibleModes.includes(ThemeMode.Dark) ? ThemeMode.Dark : visibleModes[0];
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <Button
        variant={variant}
        size='default'
        className={cn(iconOnly ? "w-8 rounded-lg p-0" : "gap-2 rounded-lg px-3", className)}
        disabled
      >
        {THEME_MODE_META[fallbackMode].icon}
        {!iconOnly ? <span className='text-sm'>{t(`mode.${fallbackMode}` as never)}</span> : null}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size='default'
          className={cn(
            // Match the notification bell's icon size (size-6 on mobile, size-4 at md).
            iconOnly ? "w-8 rounded-lg p-0 [&_svg]:size-6 md:[&_svg]:size-4" : "gap-2 rounded-lg px-3",
            className,
          )}
        >
          {THEME_MODE_META[displayMode].icon}
          {!iconOnly ? <span className='text-sm'>{t(`mode.${displayMode}` as never)}</span> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-40'>
        {visibleModes.map((mode) => (
          <DropdownMenuItem key={mode} onSelect={() => setMode(mode)}>
            <span className='flex w-full items-center gap-2'>
              {THEME_MODE_META[mode].icon}
              <span>{t(`mode.${mode}` as never)}</span>
              {activeMode === mode ? <CheckIcon className='ml-auto size-4 text-primary' /> : null}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
